/**
 * BuildingPadsConfirmation Component
 * Review/confirmation page for building pads section
 */
import React, { useState } from "react";
import FilePreviewModalSimple from "../FileUpload/FilePreviewModalSimple";
import LabeledImageUpload from "./LabeledImageUpload";

const BuildingPadsConfirmation = ({
  currentData,
  onEdit,
  onConfirm,
  onBack,
  canGoBack = false,
  isCompleted = false,
  onDataUpdate,
  projectId,
  clientId,
}) => {
  const [previewImage, setPreviewImage] = useState(null);
  const renderYesNoAnswer = (label, value, editKey) => {
    if (value === undefined || value === null) return null;

    return (
      <div style={{ marginBottom: "15px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "15px",
                color: "var(--text-primary)",
              }}
            >
              {label}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === true
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === true ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                Yes
              </button>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === false
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === false ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                No
              </button>
            </div>
          </div>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>
      </div>
    );
  };

  const renderDataSection = (title, data, editKey) => {
    if (!data || Object.keys(data).length === 0) return null;

    return (
      <div
        style={{
          marginBottom: "25px",
          padding: "15px",
          backgroundColor: "var(--surface-tertiary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          borderRadius: "4px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h4>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          {Object.entries(data).map(([key, value]) => {
            // Skip N/A flag fields (don't display them)
            if (key.includes("_na")) return null;

            // Skip fields where corresponding _na field is true
            const naFieldName = `${key}_na`;
            if (currentData[naFieldName] === true) return null;

            // Skip fields with 'N/A' value
            if (value === "N/A") return null;

            // Format the key name
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());

            // Handle different value types
            let displayValue = value;
            let isImageField = false;

            if (Array.isArray(value)) {
              isImageField = key.includes("image");
              if (!isImageField) {
                displayValue = `${value.length} item(s)`;
              }
            } else if (value === null || value === undefined || value === "") {
              displayValue = "—";
            }

            // Render editable image upload component
            if (isImageField) {
              return (
                <div key={key} style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {formattedKey}:
                  </span>
                  <LabeledImageUpload
                    images={value || []}
                    onImagesChange={(updatedImages) => {
                      if (onDataUpdate) {
                        onDataUpdate(key, updatedImages);
                      }
                    }}
                    projectId={projectId}
                    clientId={clientId}
                    hideUploadBox={true}
                  />
                </div>
              );
            }

            return (
              <div key={key} style={{ display: "flex", gap: "10px" }}>
                <span style={{ fontWeight: "500", minWidth: "200px" }}>
                  {formattedKey}:
                </span>
                <span>{displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="building-pads-confirmation" style={{ padding: "20px" }}>
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          padding: "20px",
          borderRadius: "4px",
        }}
      >
        <h3
          style={{
            margin: "0 0 25px 0",
            fontSize: "20px",
            paddingBottom: "15px",
            borderBottom: "2px solid var(--border-secondary)",
          }}
        >
          Building Pads
        </h3>

        {/* Yes/No Questions */}
        <div style={{ marginBottom: "25px" }}>
          {renderYesNoAnswer(
            "Are there commercial or large building pads?",
            currentData.has_commercial_pads,
            "has_commercial_pads",
          )}
          {currentData.has_commercial_pads &&
            renderYesNoAnswer(
              "Are there elevations in plan view?",
              currentData.commercial_has_plan_elevations,
              "commercial_has_plan_elevations",
            )}
          {currentData.has_commercial_pads &&
            renderYesNoAnswer(
              "Are there elevations in profile view?",
              currentData.commercial_has_profile_elevations,
              "commercial_has_profile_elevations",
            )}
          {renderYesNoAnswer(
            "Are there residential building pads?",
            currentData.has_residential_pads,
            "has_residential_pads",
          )}
          {currentData.has_residential_pads &&
            renderYesNoAnswer(
              "Are there elevations in plan view?",
              currentData.residential_has_plan_elevations,
              "residential_has_plan_elevations",
            )}
          {renderYesNoAnswer(
            "Are there any pad hold downs requested?",
            currentData.has_pad_hold_downs,
            "has_pad_hold_downs",
          )}
          {renderYesNoAnswer(
            "Are swales to be included in the surface?",
            currentData.swales_included_in_surface,
            "swales_included_in_surface",
          )}
        </div>

        {/* Commercial Plan View Data */}
        {currentData.commercial_has_plan_elevations &&
          renderDataSection(
            "Commercial Building Pads - Plan View",
            {
              plan_images: currentData.commercial_plan_images,
              ff_elevation: currentData.commercial_ff_elevation,
              other_elevations: currentData.commercial_other_elevations,
            },
            "commercial_plan_form",
          )}

        {/* Commercial Profile View Data */}
        {currentData.commercial_has_profile_elevations &&
          renderDataSection(
            "Commercial Building Pads - Profile View",
            {
              profile_images: currentData.commercial_profile_images,
              profile_ff_elevation: currentData.commercial_profile_ff_elevation,
              profile_other_elevations:
                currentData.commercial_profile_other_elevations,
            },
            "commercial_profile_form",
          )}

        {/* Residential Plan View Data */}
        {currentData.residential_has_plan_elevations &&
          renderDataSection(
            "Residential Building Pads - Plan View",
            {
              plan_images: currentData.residential_plan_images,
              ff_elevation: currentData.residential_ff_elevation,
              garage_elevation: currentData.residential_garage_elevation,
              pad_elevation: currentData.residential_pad_elevation,
              other_elevations: currentData.residential_other_elevations,
            },
            "residential_plan_form",
          )}

        {/* Hold Down Details */}
        {currentData.has_pad_hold_downs &&
          renderDataSection(
            "Pad Hold Down Details",
            {
              hold_down_depth: currentData.hold_down_depth,
              communicated_by: currentData.hold_down_communicated_by,
              backing_images: currentData.hold_down_backing_images,
            },
            "hold_down_depth",
          )}

        {/* Swales Exclusion Details */}
        {currentData.swales_included_in_surface === false &&
          renderDataSection(
            "Swales Exclusion Details",
            {
              communicated_by: currentData.swales_exclusion_communicated_by,
              backing_images: currentData.swales_exclusion_backing_images,
            },
            "swales_exclusion_check",
          )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          marginTop: "25px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
        }}
      >
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
        )}

        {/* Only show Confirm & Continue button if not yet completed */}
        {!isCompleted ? (
          <button
            onClick={() => {
              console.log(
                "[BuildingPadsConfirmation] ===== BUTTON CLICKED =====",
              );
              console.log(
                "[BuildingPadsConfirmation] onConfirm function:",
                onConfirm,
              );
              console.log(
                "[BuildingPadsConfirmation] isCompleted:",
                isCompleted,
              );
              console.log("[BuildingPadsConfirmation] Calling onConfirm()...");
              onConfirm();
              console.log(
                "[BuildingPadsConfirmation] onConfirm() called successfully",
              );
            }}
            style={{
              padding: "var(--space-3) var(--space-8)",
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-semibold)",
              border: "1px solid var(--cta-orange)",
              backgroundColor: "var(--cta-orange)",
              color: "white",
              cursor: "pointer",
              borderRadius: "var(--radius-md)",
              transition: "all var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange-hover)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange)";
            }}
          >
            Confirm & Continue
          </button>
        ) : (
          console.log(
            "[BuildingPadsConfirmation] Button hidden because isCompleted=true",
          )
        )}
      </div>
      {/* Image Preview Modal */}
      <FilePreviewModalSimple
        file={previewImage}
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
};

export default BuildingPadsConfirmation;
